import * as React from 'react';
import './AdminHome.css';
import './AdminCampainTemp.css';
import { Card, CardContent, CardHeader, CardTitle } from '../../Components/Card';
import { toast } from 'react-toastify';
import { AdminIcon } from '../../Icons/AdminIcon';
import { PromoCode, PromoCodeService, PromoCodeVersion } from '../../DataService/CampainService';
import { DiscountType } from '../../Types/PromoCodeTypes';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../Components/Tabs';
import { CampainCardView } from './AdminCampainComps/CampainCardView';
import { FreebieCardView } from './AdminCampainComps/FreebieCardView';
import { CampaignCodeForm } from './AdminCampainComps/CampaignCodeForm';
import { FreebieWizard } from './AdminCampainComps/FreebieWizard';
import { FreebieService, FreebieDto, FreebieSummaryDto } from '../../DataService/FreebieService';

export const promoCodeSchema: Record<string, { type: string; min?: number; max?: number; required?: boolean; values?: string[] }> = {
  code: { type: 'string', min: 3, max: 50, required: true },
  description: { type: 'string', min: 1, max: 200, required: true },
  discountType: { type: 'enum', values: ['percentage', 'fixed'], required: true },
  discountValue: { type: 'number', min: 0, max: 100000, required: true },
  maxDiscountAmount: { type: 'number', min: 0 },
  startDate: { type: 'string', min: 1, required: true },
  endDate: { type: 'string', min: 1, required: true },
  isActive: { type: 'boolean', required: true },
  usageLimit: { type: 'number', min: 0 },
  perUserLimit: { type: 'number', min: 0 },
};

type CampaignMode = 'list' | 'create' | 'edit';
type FreebieMode = 'list' | 'create' | 'edit';

export const AdminCampainTemp = () => {
  const [promoCodes, setPromoCodes] = React.useState<PromoCode[]>([]);
  const [freebies, setFreebies] = React.useState<FreebieSummaryDto[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isFreebieLoading, setIsFreebieLoading] = React.useState(false);
  const [campaignMode, setCampaignMode] = React.useState<CampaignMode>('list');
  const [freebieMode, setFreebieMode] = React.useState<FreebieMode>('list');
  const [selectedPromoCode, setSelectedPromoCode] = React.useState<PromoCode | null>(null);
  const [selectedFreebie, setSelectedFreebie] = React.useState<FreebieDto | null>(null);
  const [promoVersionsById, setPromoVersionsById] = React.useState<Record<number, PromoCodeVersion[]>>({});

  const loadPromoCodes = React.useCallback(async () => {
    setIsLoading(true);
    const codes = await PromoCodeService.getAll();
    setPromoCodes(codes);
    const versionEntries = await Promise.all(
      codes.map(async code => {
        try {
          const versions = await PromoCodeService.getVersions(code.id);
          return [code.id, versions] as const;
        } catch {
          return [code.id, []] as const;
        }
      })
    );
    setPromoVersionsById(Object.fromEntries(versionEntries));
    setIsLoading(false);
  }, []);

  const loadFreebies = React.useCallback(async () => {
    setIsFreebieLoading(true);
    const items = await FreebieService.getAll();
    setFreebies(items);
    setIsFreebieLoading(false);
  }, []);

  React.useEffect(() => {
    loadPromoCodes();
    loadFreebies();
  }, [loadPromoCodes, loadFreebies]);

  const handlePromoSubmit = async (data: PromoCode) => {
    const result = selectedPromoCode
      ? await PromoCodeService.update(selectedPromoCode.id, data)
      : await PromoCodeService.create(data);

    toast.success(result.message);
    setSelectedPromoCode(null);
    setCampaignMode('list');
    await loadPromoCodes();
  };

  const handlePromoEdit = (promoCode: PromoCode) => {
    setSelectedPromoCode(promoCode);
    setCampaignMode('edit');
  };

  const handlePromoDelete = async (id: number) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna kampanjkod?')) return;
    const result = await PromoCodeService.delete(id);
    toast.success(result.message);
    await loadPromoCodes();
  };

  const handleFreebieCreate = () => {
    setSelectedFreebie(null);
    setFreebieMode('create');
  };

  const handleFreebieEdit = async (freebie: FreebieSummaryDto) => {
    try {
      const full = await FreebieService.getById(freebie.id);
      setSelectedFreebie(full);
      setFreebieMode('edit');
    } catch (error) {
      toast.error('Kunde inte läsa in freebie för redigering');
    }
  };

  const handleFreebieDelete = async (id: string) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna freebie?')) return;
    const result = await FreebieService.delete(id);
    toast.success(result.message);
    await loadFreebies();
  };

  const totalActiveCodes = promoCodes.filter(pc => pc.isActive).length;
  const totalUsage = promoCodes.reduce((sum, pc) => sum + pc.usageCount, 0);
  const totalRevenueLoss = promoCodes.reduce((sum, pc) => {
    if (pc.discountType === DiscountType.FixedAmount) return sum + (pc.discountValue * pc.usageCount);
    return sum;
  }, 0);

    return (
    <div className="admin-page-shell admin-campain-page-shell">
      <header className="admin-page-header admin-campain-page-header">
        <div>
          <h1 className="admin-page-title">Kampanjer</h1>
          <p className="admin-page-subtitle">Hantera kampanjkoder och freebies i ett tydligt arbetsflöde.</p>
        </div>
        <span className="admin-pill">{totalActiveCodes} aktiva koder</span>
      </header>

      <section className="admin-surface-grid admin-campain-stats">
        <Card className="admin-surface-card" disableHover>
          <CardContent className="admin-campain-card-content">
            <div className="admin-campain-flex-row-center">
              <AdminIcon.UserGroup />
              <div>
                <p className="admin-campain-stat-label">Totala användningar</p>
                <p className="admin-campain-stat-value admin-campain-text-green">{totalUsage}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="admin-surface-card" disableHover>
          <CardContent className="admin-campain-card-content">
            <div className="admin-campain-flex-row-center">
              <AdminIcon.Graph />
              <div>
                <p className="admin-campain-stat-label">Total rabatt given</p>
                <p className="admin-campain-stat-value admin-campain-text-purple">{totalRevenueLoss} kr</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      <section className="admin-surface-card admin-campain-main-card">
        <Tabs defaultValue="campain">
          <TabsList className="admin-campain-main-tabs">
            <TabsTrigger value="campain">Kampanjkoder</TabsTrigger>
            <TabsTrigger value="freebe">Freebies</TabsTrigger>
          </TabsList>

          <TabsContent value="campain">
            <div className="admin-campain-workspace">
              <Tabs key={campaignMode} defaultValue={campaignMode}>
                <TabsList className="admin-campain-subtabs">
                  <TabsTrigger value="list">Lista</TabsTrigger>
                  <TabsTrigger value="create">Skapa</TabsTrigger>
                  <TabsTrigger value="edit">Redigera</TabsTrigger>
                </TabsList>

                <TabsContent value="list">
                  <CampainCardView
                    isLoading={isLoading}
                    promoCodes={promoCodes}
                    onCreate={() => setCampaignMode('create')}
                    onEdit={handlePromoEdit}
                    onDelete={handlePromoDelete}
                  />
                  <Card className="admin-surface-card" disableHover>
                    <CardHeader>
                      <CardTitle>Versionshistorik</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {promoCodes.map(code => (
                          <div key={code.id} style={{ border: '1px solid hsl(var(--border))', borderRadius: 10, padding: '0.75rem' }}>
                            <strong>{code.code}</strong>
                            <div style={{ display: 'grid', gap: '0.4rem', marginTop: '0.5rem' }}>
                              {(promoVersionsById[code.id] ?? []).map(version => (
                                <div key={version.id} style={{ padding: '0.5rem 0.75rem', borderRadius: 8, background: 'hsl(var(--muted))' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <span>v{version.versionNumber}</span>
                                    <span>{version.changeType}</span>
                                  </div>
                                  <div style={{ fontSize: 12, opacity: 0.75 }}>{version.discountType === 0 ? `${version.discountValue}%` : `${version.discountValue} kr`}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="create">
                  <CampaignCodeForm
                    action="create"
                    onSubmit={handlePromoSubmit}
                    onCancel={() => setCampaignMode('list')}
                  />
                </TabsContent>

                <TabsContent value="edit">
                  <CampaignCodeForm
                    action="edit"
                    selectedPromoCode={selectedPromoCode}
                    onSubmit={handlePromoSubmit}
                    onCancel={() => {
                      setSelectedPromoCode(null);
                      setCampaignMode('list');
                    }}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>

          <TabsContent value="freebe">
            <div className="admin-campain-workspace">
              <Tabs key={freebieMode} defaultValue={freebieMode}>
                <TabsList className="admin-campain-subtabs">
                  <TabsTrigger value="list">Lista</TabsTrigger>
                  <TabsTrigger value="create">Skapa</TabsTrigger>
                  <TabsTrigger value="edit">Redigera</TabsTrigger>
                </TabsList>

                <TabsContent value="list">
                  <FreebieCardView
                    isLoading={isFreebieLoading}
                    freebies={freebies}
                    onCreate={handleFreebieCreate}
                    onEdit={handleFreebieEdit}
                    onDelete={handleFreebieDelete}
                  />
                </TabsContent>

                <TabsContent value="create">
                  <FreebieWizard
                    mode="create"
                    onCancel={() => setFreebieMode('list')}
                    onSaved={async () => {
                      await loadFreebies();
                    }}
                  />
                </TabsContent>

                <TabsContent value="edit">
                  <FreebieWizard
                    mode="edit"
                    freebie={selectedFreebie}
                    onCancel={() => {
                      setSelectedFreebie(null);
                      setFreebieMode('list');
                    }}
                    onSaved={async () => {
                      await loadFreebies();
                    }}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
};
